/**
 * Notifies Unity WebGL (BrowserBridge) which IVS participant id is publishing screen share,
 * so NetworkedStreamController can match AVPro fake-url participantId (see VirtualVenues BrowserBridge IvsScreenSharePublisher).
 */

/** Cast-only shape — do not intersect global `Window` (conflicts with UnityGame.tsx augmentation). */
function getUnityBridgeHandles(): {
  sendMessageToUnity?: (gameObjectName: string, methodName: string, parameter?: string | number) => void;
  unityInstance?: { SendMessage?: (go: string, method: string, value?: string) => void };
} {
  return window as unknown as {
    sendMessageToUnity?: (gameObjectName: string, methodName: string, parameter?: string | number) => void;
    unityInstance?: { SendMessage?: (go: string, method: string, value?: string) => void };
  };
}

function deliverReceivePayload(json: string): void {
  const w = getUnityBridgeHandles();
  try {
    if (typeof w.sendMessageToUnity === 'function') {
      w.sendMessageToUnity('BrowserBridge', 'ReceivePayload', json);
      return;
    }
    w.unityInstance?.SendMessage?.('BrowserBridge', 'ReceivePayload', json);
  } catch (e) {
    console.warn('[ivsScreenShareUnityBridge] SendMessage failed', e);
  }
}

export interface ScreenShareSession {
  participantId: string;
  roomId: string;
  stageArn: string;
  sessionId: string;
}

export function sendIvsScreenSharePublisherToUnity(session: ScreenShareSession, active: boolean): void {
  const inner = JSON.stringify({
    ...session,
    active
  });
  const wrapped = JSON.stringify({
    eventType: 'IvsScreenSharePublisher',
    payload: inner
  });
  deliverReceivePayload(wrapped);
}

/** Retry schedule mirrors delayed LocalPlayer / Unity readiness. */
const RETRY_DELAYS_MS = [0, 400, 1200, 3000];

export function sendIvsScreenSharePublisherToUnityWithRetries(session: ScreenShareSession, active: boolean): () => void {
  sendIvsScreenSharePublisherToUnity(session, active);
  const timers = RETRY_DELAYS_MS.filter(delay => delay > 0).map(delay => setTimeout(() => {
    sendIvsScreenSharePublisherToUnity(session, active);
  }, delay));
  return () => timers.forEach(clearTimeout);
}

/** One owner per panel. Viewers never announce a stop; replacing/stopping cancels old retries. */
export function createScreenShareAnnouncements() {
  let current: ScreenShareSession | undefined;
  let cancelRetries: (() => void) | undefined;
  return {
    start(session: ScreenShareSession) {
      cancelRetries?.();
      if (current) sendIvsScreenSharePublisherToUnity(current, false);
      current = session;
      cancelRetries = sendIvsScreenSharePublisherToUnityWithRetries(session, true);
    },
    stop() {
      cancelRetries?.();
      cancelRetries = undefined;
      if (!current) return;
      const stopped = current;
      current = undefined;
      // Stop is delivered immediately before room/runtime teardown. Never leave orphan timers.
      sendIvsScreenSharePublisherToUnity(stopped, false);
    }
  };
}
